import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { MAX_DUTY_COUNT_IDS, type DutyCountsDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3c §4b] THE 90-DAY DUTY COUNTS, LOADED BEHIND THE NAMES.
 *
 *  ⚠️ NOT THROUGH THE SEARCH API. That aggregation on `GET /duties/:id/candidates` would run on
 *  every keystroke, which is the responsiveness [P4] built the search sheet to protect — twelve
 *  names in ninety seconds. So the names render first and the numbers arrive behind them, five
 *  officers at a time, with a skeleton in each slot until its own batch answers.
 *
 *  ⚠️ THE FRONTIER ADVANCES ON **SETTLE**, NOT ON SUCCESS. A batch that fails must not stall
 *  every batch behind it: its five rows render `—` and the queue moves on. A failure here is a
 *  missing convenience, not a missing name.
 *
 *  ⚠️ AND THE RESET IS BY KEY IDENTITY RATHER THAN CANCELLATION — which is STRONGER. When the
 *  search term changes the id list changes, so every batch key changes with it, and any request
 *  still in flight for the old term resolves into a cache entry nothing renders. There is no
 *  tail of results landing in a list that has moved on, because a landed result has nowhere to
 *  land. Nothing here needs an `AbortController`; TanStack's own `signal` does the rest.
 *
 *  ⚠️ THE FRONTIER IS ADJUSTED DURING RENDER, NEVER IN AN EFFECT. React's documented pattern,
 *  and the one `OfficerSearchSheet` already uses to clear its query when the cursor advances —
 *  an effect would render one frame with the previous term's frontier, which on a fast typist is
 *  a visible flicker of somebody else's numbers.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** Roughly five at a time, per the brief. Small enough that the first numbers appear at once. */
export const DUTY_COUNT_BATCH = 5;

const chunk = (ids: readonly string[]): string[][] => {
  const out: string[][] = [];
  for (let index = 0; index < ids.length; index += DUTY_COUNT_BATCH) {
    out.push([...ids.slice(index, index + DUTY_COUNT_BATCH)]);
  }
  return out;
};

export type DutyCountState = Map<string, number>;

export const useDutyCounts = (input: {
  open: boolean;
  dutyId: string;
  slotRef: string;
  officerIds: readonly string[];
}): { counts: DutyCountState; pending: ReadonlySet<string> } => {
  /*
   * ⚠️ BOUNDED BY THE SERVER'S OWN LIMIT, not by the batch size. `MAX_DUTY_COUNT_IDS` is the
   * candidate list's bound; asking about more than the list can hold would be a 422 for a
   * question the screen cannot render an answer to.
   */
  const ids = input.officerIds.slice(0, MAX_DUTY_COUNT_IDS);
  const batches = chunk(ids);
  const fingerprint = `${input.slotRef}|${ids.join(',')}`;

  const [frontier, setFrontier] = useState(0);
  const [renderedFor, setRenderedFor] = useState(fingerprint);
  if (fingerprint !== renderedFor) {
    setRenderedFor(fingerprint);
    setFrontier(0);
  }

  const results = useQueries({
    queries: batches.map((batch, index) => ({
      queryKey: queryKeys.fill.dutyCounts(input.dutyId, input.slotRef, batch.join(',')),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<DutyCountsDto>(`/duties/${input.dutyId}/duty-counts`, {
          /* ⚠️ COMMA-JOINED, because this client's `query` takes scalars only (`params.set`).
             An ObjectId is 24 hex characters and can never contain the separator — see the
             schema, which accepts this form and repeated keys alike. */
          query: { slotRef: input.slotRef, officerIds: batch.join(',') },
          signal,
        }),
      enabled: input.open && index <= frontier,
      staleTime: 30_000,
    })),
  });

  const counts: DutyCountState = new Map();
  const pending = new Set<string>();

  results.forEach((result, index) => {
    const batch = batches[index] ?? [];
    if (result.data) {
      for (const row of result.data.counts) counts.set(row.officerId, row.count);
      return;
    }
    /* ⚠️ A FAILED BATCH IS NOT PENDING — its rows render `—` rather than a skeleton for ever. */
    if (!result.isError) for (const id of batch) pending.add(id);
  });

  /*
   * ⚠️ ADVANCE WHEN THE FRONT BATCH HAS SETTLED, either way. Done during render rather than in
   * an effect, for the reason in the header — and guarded so it cannot loop past the end.
   */
  const front = results[frontier];
  if (front && !front.isPending && frontier < batches.length - 1) setFrontier(frontier + 1);

  return { counts, pending };
};
